import {FC, useCallback, useEffect, useMemo, useState} from 'react'
import {Card, Dropdown, Spinner} from 'react-bootstrap'
import {
  AllocateCreditSubClientMode,
  CreditOverview,
  SubClient,
} from '../../../../../services/cellcast.v2.types'
import WriteCredit from '../Modal/WriteCredit'
import CreditHistory from './CreditHistory'
import {useSelector} from 'react-redux'
import {FixedTwoDecimal} from '../../../../Utils/_helpers/string'

export type BillingProps = {
  reload: () => void
  isFetching: boolean
  credit: CreditOverview
  data: SubClient | undefined
}

const Billing: FC<BillingProps> = ({isFetching, credit, data, reload}) => {
  const [allocateMode, setAllocateMode] = useState<AllocateCreditSubClientMode>(
    AllocateCreditSubClientMode.add
  )
  const [showAllocateModal, setShowAllocateModal] = useState(false)
  const [smsCredit, setSmsCredit] = useState(0)
  const [mmsCredit, setMMsCredit] = useState(0)

  const userCredit = useSelector((state: any) => state?.userDetails?.userDetails?.data)
  useEffect(() => {
    if (allocateMode === AllocateCreditSubClientMode.add) {
      const smsCredit =
        userCredit && userCredit.credits && userCredit.credits.sms ? userCredit.credits.sms : 0
      const mmsCredit =
        userCredit && userCredit.credits && userCredit.credits.mms ? userCredit.credits.mms : 0
      setSmsCredit(smsCredit)
      setMMsCredit(mmsCredit)
    } else {
      const smsCredit = credit.allocatedSms + credit.purchasedSms - credit.usedSms
      const mmsCredit = credit.allocatedMms + credit.purchasedMms - credit.usedMms
      setSmsCredit(smsCredit)
      setMMsCredit(mmsCredit)
    }
  }, [
    allocateMode,
    credit.allocatedMms,
    credit.allocatedSms,
    credit.usedMms,
    credit.usedSms,
    userCredit,
  ])

  // memoise component
  const WriteCreditModal = useMemo(
    () => (
      <WriteCredit
        handleClose={() => setShowAllocateModal(false)}
        callbackFn={() => {
          setShowAllocateModal(false)
          reload()
        }}
        mode={allocateMode}
        show={showAllocateModal}
        userId={data ? data._id : ''}
        userName={allocateMode === AllocateCreditSubClientMode.add ? userCredit.user?.name : data?.name}
        smsCredit={smsCredit}
        mmsCredit={mmsCredit}
      />
    ),
    [allocateMode, data, mmsCredit, reload, showAllocateModal, smsCredit]
  )

  return (
    <>
      {WriteCreditModal}
      <div className='col-md-12'>
        <Card className='mb-6'>
          {isFetching ? (
            <Card.Body className='pt-9 pb-0 h-500'>
              <div className='d-flex flex-wrap flex-sm-nowrap flex-column align-items-center  mb-10'>
                <Spinner className='mb-5' />
                <span>Fetching data ....</span>
              </div>
            </Card.Body>
          ) : (
            <Card.Body>
              <div className='d-flex flex-wrap flex-sm-nowrap'>
                <div className='flex-grow-1'>
                  <div className='d-flex justify-content-between align-items-start flex-wrap mb-2'>
                    <div className='d-flex flex-column'>
                      <div className='d-flex align-items-center mb-2'>
                        <a
                          href='#'
                          className='text-gray-900 text-hover-primary fs-2 fw-bolder me-1'
                        >
                          Credit Balance
                        </a>
                      </div>
                    </div>

                    <div className='d-flex mb-3'>
                      <Dropdown>
                        <Dropdown.Toggle
                          className='btn btn-light-primary btn-sm me-2'
                          id='dropdown-basic'
                        >
                          manage credits
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                          <Dropdown.Item
                            className='menu-content  p-3 fs-8 text-uppercase'
                            onClick={() => {
                              if (data && data._id) {
                                setAllocateMode(AllocateCreditSubClientMode.add)
                                setShowAllocateModal(true)
                              }
                            }}
                          >
                            Add
                          </Dropdown.Item>
                          <Dropdown.Item
                            className='menu-content  p-3 fs-8 text-uppercase'
                            onClick={() => {
                              if (data && data._id) {
                                setAllocateMode(AllocateCreditSubClientMode.remove)
                                setShowAllocateModal(true)
                              }
                            }}
                          >
                            remove
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                  </div>

                  <div className='d-flex flex-wrap flex-stack'>
                    <div className='d-flex flex-column flex-grow-1'>
                      <div className='d-flex flex-wrap justify-content-between gap-3'>
                        <div className='border border-gray-300 border-dashed rounded py-3 px-4 mb-3 flex-grow-1'>
                          <div className='d-flex align-items-center'>
                            <div className='fs-2 fw-bolder text-success'>
                              {FixedTwoDecimal(credit.allocatedSms + credit.purchasedSms - credit.usedSms)} SMS
                            </div>
                          </div>
                        </div>
                        <div className='border border-gray-300 border-dashed rounded py-3 px-4 mb-3 flex-grow-1'>
                          <div className='d-flex align-items-center'>
                            <div className='fs-2 fw-bolder text-info'>
                              {FixedTwoDecimal(credit.allocatedMms + credit.purchasedMms - credit.usedMms)} MMS
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card.Body>
          )}
        </Card>
      </div>
      <CreditHistory user={data} reload={isFetching} />
    </>
  )
}

export default Billing
